/* eslint-disable sort-imports */
import { ComboBox } from 'src/components/ui/combobox';
import { DatePickerInput } from 'src/components/date-picker/date-picker-input';
import FooterPrivacy from 'src/views/auth/components/baseTemplate/FooterPrivacy';
import { BulletinTextField as Field } from 'src/components/bulletins-form';
import DialogModal from 'src/components/ui/dialogModal';
import {
    CREATE_CRITICAL_UPDATE_PATH,
    CriticalOrganizationUpdatesTabs,
    VIEW_CRITICAL_UPDATE_PATH,
} from 'src/components/critical-organization-updates/tabs';
import { RadioButton } from 'src/components/ui/radioButton';
import Notification, {
    NotificationType,
} from 'src/components/ui/notification/Notification';
import Spinner from 'src/components/ui/spinner';
import React, { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { CriticalOrganizationUpdatesService } from 'src/services/critical-organization-updates.service';
import {
    CRITICAL_NEWS_DOCUMENT_TYPE,
    CRITICAL_NEWS_LANGUAGE_CODES,
    CRITICAL_NEWS_LANGUAGES,
    CRITICAL_NEWS_TIME_OPTIONS,
    DEFAULT_END_TIME,
    DEFAULT_START_TIME,
    isBodyEmpty,
    sanitizeNewsHtml,
    toNewsDate,
    toNewsTime,
} from 'src/utils/critical-organization-updates';
import {
    SYSTEM_ADMIN_COU_LEAVE_MODAL_BODY,
    SYSTEM_ADMIN_COU_LEAVE_MODAL_CANCEL,
    SYSTEM_ADMIN_COU_LEAVE_MODAL_CONFIRM,
    SYSTEM_ADMIN_COU_LEAVE_MODAL_TITLE,
} from 'src/localization/resouceKeys';
import RichTextEditor from 'src/components/critical-organization-updates/rich-text-editor';

import 'src/components/critical-organization-updates/styles.scss';

const EMPTY_ERRORS = 0;
const SCROLL_TOP = 0;
const MONTH_INDEX_OFFSET = 1;
const HEADLINE_MAX_LENGTH = 255;
const SYSTEM_ADMIN_PATH = '/dashboard/systemadmin';
const SELECT_DISTRIBUTION_RULE_PATH =
    '/dashboard/systemadmin/view-distribution-rule';

interface ICriticalUpdateForm {
    headline: string;
    bodyCustom: string;
    language: string;
    startDate: string;
    startTime: string;
    endDate: string;
    endTime: string;
    distributionRule: string;
    distributionId: string;
}

const INITIAL_FORM: ICriticalUpdateForm = {
    headline: '',
    bodyCustom: '',
    language: CRITICAL_NEWS_LANGUAGES[0],
    startDate: '',
    startTime: '00:00 AM',
    endDate: '',
    endTime: '',
    distributionRule: '',
    distributionId: '',
};

interface ICriticalUpdateNavigationState {
    selectedRule?: { distributionId: string; ruleName: string };
    formState?: ICriticalUpdateForm;
}

type CriticalUpdateField =
    | 'headline'
    | 'body'
    | 'startDate'
    | 'endDate'
    | 'distributionRule';
type CriticalUpdateErrors = Partial<Record<CriticalUpdateField, string>>;

const parseYmdToDate = (value: string): Date | undefined => {
    const [year, month, day] = value.split('-').map(Number);
    return year && month && day
        ? new Date(year, month - MONTH_INDEX_OFFSET, day)
        : undefined;
};

const CreateCriticalOrganizationUpdates: React.FC = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const [form, setForm] = useState<ICriticalUpdateForm>(INITIAL_FORM);
    const [fieldErrors, setFieldErrors] = useState<CriticalUpdateErrors>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');
    const [submitError, setSubmitError] = useState(false);
    const [isSubmitMessageVisible, setIsSubmitMessageVisible] = useState(false);
    const [isLeaveDialogOpen, setIsLeaveDialogOpen] = useState(false);

    const text = (key: string, defaultValue: string) =>
        t(`SystemAdmin.CriticalOrganizationUpdates.${key}`, { defaultValue });

    const isFormComplete = Boolean(
        form.headline.trim() &&
            !isBodyEmpty(form.bodyCustom) &&
            form.startDate &&
            form.distributionRule,
    );

    const isDirty = Boolean(
        form.headline ||
            !isBodyEmpty(form.bodyCustom) ||
            form.startDate ||
            form.endDate ||
            form.distributionRule ||
            form.language !== INITIAL_FORM.language ||
            form.startTime !== INITIAL_FORM.startTime ||
            form.endTime !== INITIAL_FORM.endTime,
    );

    useEffect(() => {
        const state = location.state as ICriticalUpdateNavigationState | null;

        if (!state?.formState && !state?.selectedRule) return;

        setForm((currentForm) => ({
            ...currentForm,
            ...state.formState,
            ...(state.selectedRule && {
                distributionRule: state.selectedRule.ruleName,
                distributionId: state.selectedRule.distributionId,
            }),
        }));
        navigate(location.pathname, { replace: true, state: null });
    }, []);

    const updateForm = (field: keyof ICriticalUpdateForm, value: string) => {
        setForm((currentForm) => ({ ...currentForm, [field]: value }));
        setFieldErrors((currentErrors) => {
            const nextErrors = { ...currentErrors };
            delete nextErrors[
                (field.startsWith('body')
                    ? 'body'
                    : field) as CriticalUpdateField
            ];
            if (
                field === 'startDate' ||
                field === 'startTime' ||
                field === 'endTime'
            ) {
                delete nextErrors.endDate;
            }
            return nextErrors;
        });
    };

    const validateForm = (): CriticalUpdateErrors => {
        const requiredMessage = t('Label.Field.Error.Required', {
            defaultValue: 'Field required',
        });
        const errors: CriticalUpdateErrors = {};

        if (!form.headline.trim()) errors.headline = requiredMessage;
        if (isBodyEmpty(form.bodyCustom)) errors.body = requiredMessage;
        if (!form.startDate) errors.startDate = requiredMessage;
        if (form.endDate && form.startDate && form.endDate < form.startDate) {
            errors.endDate = text(
                'EndBeforeStartError',
                'End date must be on or after the start date.',
            );
        } else if (
            form.endDate &&
            form.endDate === form.startDate &&
            toNewsTime(form.endTime, DEFAULT_END_TIME) <=
                toNewsTime(form.startTime, DEFAULT_START_TIME)
        ) {
            errors.endDate = text(
                'EndBeforeStartTimeError',
                'End time must be after the start time.',
            );
        }
        if (!form.distributionRule) errors.distributionRule = requiredMessage;

        return errors;
    };

    const showMessage = (message: string, isError: boolean) => {
        window.scrollTo(SCROLL_TOP, SCROLL_TOP);
        setSubmitError(isError);
        setSubmitMessage(message);
        setIsSubmitMessageVisible(true);
    };

    const handleSelectRule = () => {
        navigate(SELECT_DISTRIBUTION_RULE_PATH, {
            state: {
                selectMode: true,
                showSelectBanner: false,
                returnTo: CREATE_CRITICAL_UPDATE_PATH,
                formState: form,
            },
        });
    };

    const handleCancel = () => {
        if (isDirty) {
            setIsLeaveDialogOpen(true);
            return;
        }
        navigate(SYSTEM_ADMIN_PATH);
    };

    const handleLeaveConfirm = () => {
        setIsLeaveDialogOpen(false);
        navigate(SYSTEM_ADMIN_PATH);
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (isSubmitting) return;

        const errors = validateForm();
        setFieldErrors(errors);
        if (Object.keys(errors).length > EMPTY_ERRORS) {
            showMessage(
                text(
                    'ValidationError',
                    'Please correct the highlighted fields before submitting.',
                ),
                true,
            );
            return;
        }

        setIsSubmitting(true);
        setIsSubmitMessageVisible(false);

        try {
            await new CriticalOrganizationUpdatesService().upsertNews({
                documentType: CRITICAL_NEWS_DOCUMENT_TYPE,
                headline: form.headline.trim(),
                distributionId: form.distributionId,
                validFromDate: toNewsDate(
                    form.startDate,
                    form.startTime,
                    DEFAULT_START_TIME,
                ),
                ...(form.endDate && {
                    validThroughDate: toNewsDate(
                        form.endDate,
                        form.endTime,
                        DEFAULT_END_TIME,
                    ),
                }),
                language: CRITICAL_NEWS_LANGUAGE_CODES[form.language],
                document: sanitizeNewsHtml(form.bodyCustom),
            });
            setForm(INITIAL_FORM);
            navigate(VIEW_CRITICAL_UPDATE_PATH, {
                state: {
                    successMessage: text(
                        'SubmitSuccess',
                        'Critical organization update has been successfully published.',
                    ),
                },
            });
        } catch {
            showMessage(
                text(
                    'SubmitError',
                    'Unable to publish the critical organization update. Please try again later.',
                ),
                true,
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderDateTime = (
        idPrefix: string,
        label: string,
        dateField: 'startDate' | 'endDate',
        timeField: 'startTime' | 'endTime',
        placeholder: string,
        minDate?: Date,
    ) => (
        <div className='c-form-field'>
            <label className='c-form-field__label' htmlFor={`${idPrefix}-date`}>
                {label}
                {dateField === 'startDate' && '*'}
            </label>
            <div className='critical-updates__datetime'>
                <div>
                    <div
                        className={`critical-updates__date-picker ${
                            fieldErrors[dateField] ? 'is-error' : ''
                        }`}
                    >
                        <DatePickerInput
                            id={`${idPrefix}-date`}
                            ariaLabel={
                                dateField === 'startDate'
                                    ? text(
                                          'StartDateAccessibleLabel',
                                          'Start date (required)',
                                      )
                                    : text(
                                          'EndDateAccessibleLabel',
                                          'End date (optional)',
                                      )
                            }
                            ariaDescribedBy={
                                fieldErrors[dateField]
                                    ? `${idPrefix}-error`
                                    : undefined
                            }
                            hasError={Boolean(fieldErrors[dateField])}
                            required={dateField === 'startDate'}
                            selectedDate={form[dateField]}
                            handleOnClose={(value) =>
                                updateForm(dateField, value)
                            }
                            {...(minDate && { minDate })}
                        />
                        <span className='critical-updates__date-placeholder'>
                            {!form[dateField] && placeholder}
                        </span>
                    </div>
                </div>
                <ComboBox
                    name={`${idPrefix}-time`}
                    ariaLabel={
                        timeField === 'startTime'
                            ? text(
                                  'StartTimeAccessibleLabel',
                                  'Start time (optional)',
                              )
                            : text(
                                  'EndTimeAccessibleLabel',
                                  'End time (optional)',
                              )
                    }
                    ariaDescribedBy={
                        fieldErrors[dateField] ? `${idPrefix}-error` : undefined
                    }
                    hasError={Boolean(fieldErrors[dateField])}
                    options={CRITICAL_NEWS_TIME_OPTIONS}
                    placeholder={text('TimePlaceholder', 'Select time')}
                    selectedOption={form[timeField]}
                    onSelect={(value: string) => updateForm(timeField, value)}
                />
            </div>
            {fieldErrors[dateField] && (
                <span className='c-error-message' id={`${idPrefix}-error`}>
                    {fieldErrors[dateField]}
                </span>
            )}
        </div>
    );

    return (
        <>
            <main className='critical-updates'>
                {submitMessage && (
                    <Notification
                        content={submitMessage}
                        type={
                            submitError
                                ? NotificationType.ERROR
                                : NotificationType.SUCCESS
                        }
                        visibility={isSubmitMessageVisible}
                        setVisibility={setIsSubmitMessageVisible}
                    />
                )}

                <div className='page-title'>
                    <h1>
                        {text('PageTitle', 'Critical Organization Updates')}
                    </h1>
                </div>

                <CriticalOrganizationUpdatesTabs active='create' />

                <section className='c-form-section critical-updates__card'>
                    <div className='c-form-section__content'>
                        <h2>
                            {text(
                                'Title',
                                'Create Critical Organization Updates',
                            )}
                        </h2>
                        <p className='critical-updates__description'>
                            {text(
                                'Description',
                                'Follow the steps below to upload critical organization updates to the Dealer Extranet system.',
                            )}
                        </p>

                        <form onSubmit={handleSubmit} noValidate>
                            <div className='critical-updates__fields'>
                                <Field
                                    id='critical-update-headline'
                                    label={text('HeadlineLabel', 'Headline')}
                                    placeholder={text(
                                        'HeadlinePlaceholder',
                                        'Enter headline here',
                                    )}
                                    maxLength={HEADLINE_MAX_LENGTH}
                                    value={form.headline}
                                    onChange={(value) =>
                                        updateForm('headline', value)
                                    }
                                    error={fieldErrors.headline}
                                />

                                <div className='c-form-field'>
                                    <label
                                        id='critical-update-body-label'
                                        className='c-form-field__label'
                                    >
                                        {text('BodyLabel', 'Body Content')}*
                                    </label>
                                    <RichTextEditor
                                        id='critical-update-body'
                                        labelledBy='critical-update-body-label'
                                        placeholder={text(
                                            'BodyPlaceholder',
                                            'Enter Critical Organization Update',
                                        )}
                                        value={form.bodyCustom}
                                        onChange={(html) =>
                                            updateForm('bodyCustom', html)
                                        }
                                        hasError={Boolean(fieldErrors.body)}
                                    />
                                    {fieldErrors.body && (
                                        <span className='c-error-message'>
                                            {fieldErrors.body}
                                        </span>
                                    )}
                                </div>

                                <div className='critical-updates__schedule'>
                                    <div className='c-form-field'>
                                        <label className='c-form-field__label'>
                                            {text('LanguageLabel', 'Language')}*
                                        </label>
                                        <RadioButton
                                            name='critical-update-language'
                                            options={CRITICAL_NEWS_LANGUAGES}
                                            labels={[
                                                text('English', 'English'),
                                                text('French', 'French'),
                                            ]}
                                            selectedOption={form.language}
                                            onSelect={(value: string) =>
                                                updateForm('language', value)
                                            }
                                        />
                                    </div>
                                    {renderDateTime(
                                        'critical-update-start',
                                        text(
                                            'StartDateLabel',
                                            'Start Date & Time (CST)',
                                        ),
                                        'startDate',
                                        'startTime',
                                        text(
                                            'StartDatePlaceholder',
                                            'Select start date',
                                        ),
                                    )}
                                    {renderDateTime(
                                        'critical-update-end',
                                        text(
                                            'EndDateLabel',
                                            'End Date & Time (CST)',
                                        ),
                                        'endDate',
                                        'endTime',
                                        text(
                                            'EndDatePlaceholder',
                                            'Select end date',
                                        ),
                                        parseYmdToDate(form.startDate),
                                    )}
                                </div>

                                <div className='c-form-field'>
                                    <label
                                        className='c-form-field__label'
                                        htmlFor='critical-update-distribution-rule'
                                    >
                                        {text(
                                            'DistributionLabel',
                                            'Choose Distribution Rule',
                                        )}
                                        *
                                    </label>
                                    <div className='critical-updates__distribution-control'>
                                        <div className='c-input'>
                                            <input
                                                id='critical-update-distribution-rule'
                                                className={`c-input__input ${
                                                    fieldErrors.distributionRule
                                                        ? 'is-error'
                                                        : ''
                                                }`}
                                                type='text'
                                                readOnly
                                                placeholder={text(
                                                    'DistributionPlaceholder',
                                                    'No distribution rule selected',
                                                )}
                                                value={form.distributionRule}
                                            />
                                        </div>
                                        <button
                                            className='c-btn c-btn--secondary'
                                            type='button'
                                            onClick={handleSelectRule}
                                        >
                                            <span className='c-btn__text'>
                                                {text(
                                                    'SelectRule',
                                                    'Select Rule',
                                                )}
                                            </span>
                                        </button>
                                    </div>
                                    {fieldErrors.distributionRule && (
                                        <span className='c-error-message'>
                                            {fieldErrors.distributionRule}
                                        </span>
                                    )}
                                    <p className='critical-updates__helper'>
                                        {text(
                                            'DistributionHelp',
                                            'Click "Select Rule" to define the targeted recipient audience in the Dealer Extranet rules engine. You will be redirected back once chosen.',
                                        )}
                                    </p>
                                </div>
                            </div>

                            <div className='critical-updates__actions'>
                                <button
                                    className='c-btn c-btn--secondary'
                                    type='button'
                                    onClick={handleCancel}
                                >
                                    <span className='c-btn__text'>
                                        {text('Cancel', 'Cancel')}
                                    </span>
                                </button>
                                <button
                                    className='c-btn'
                                    type='submit'
                                    disabled={isSubmitting || !isFormComplete}
                                >
                                    <span className='c-btn__text'>
                                        {isSubmitting ? (
                                            <>
                                                <Spinner />
                                                {text(
                                                    'Submitting',
                                                    'Publishing...',
                                                )}
                                            </>
                                        ) : (
                                            text('Submit', 'Publish')
                                        )}
                                    </span>
                                </button>
                            </div>
                        </form>
                    </div>
                </section>
            </main>
            <DialogModal
                titleContent={t(SYSTEM_ADMIN_COU_LEAVE_MODAL_TITLE)}
                bodyContent={t(SYSTEM_ADMIN_COU_LEAVE_MODAL_BODY)}
                open={isLeaveDialogOpen}
                cancelBtnLabel={t(SYSTEM_ADMIN_COU_LEAVE_MODAL_CANCEL)}
                confirmBtnLabel={t(SYSTEM_ADMIN_COU_LEAVE_MODAL_CONFIRM)}
                onCancelHandler={() => setIsLeaveDialogOpen(false)}
                onConfirmHandler={handleLeaveConfirm}
            />
            <div className='critical-updates__privacy-footer'>
                <FooterPrivacy />
            </div>
        </>
    );
};

export default CreateCriticalOrganizationUpdates;
